import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, Users, LogOut, Menu, X, Shield, DollarSign, BarChart3, 
  RefreshCw, Settings, FileText, UserCog, Activity, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

const PlatformAdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/unified-login');
  };

  const navItems = [
    { path: '/platform-admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/platform-admin/users', icon: UserCog, label: 'User Management' },
    { path: '/platform-admin/billing', icon: DollarSign, label: 'Billing & Revenue' },
    { path: '/platform-admin/analytics', icon: BarChart3, label: 'KPIs & Analytics' },
    { path: '/platform-admin/activation', icon: Activity, label: 'Activation' },
    { path: '/platform-admin/challenges', icon: Trophy, label: 'Weekly Challenges' },
    { path: '/platform-admin/transitions', icon: RefreshCw, label: 'Coach Transitions' },
    { path: '/platform-admin/settings', icon: Settings, label: 'Platform Settings' },
    { path: '/platform-admin/audit', icon: FileText, label: 'Audit Log' },
  ];

  const isActive = (path) => {
    if (path === '/platform-admin') return location.pathname === '/platform-admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar toggle — top-right to avoid overlapping page title */}
      <button 
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-card border border-border rounded-sm shadow-lg"
        data-testid="admin-mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-card border-r border-border
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/platform-admin" className="flex items-center gap-2">
              <Shield className="text-primary" size={24} />
              <span className="font-heading text-lg">Platform Admin</span>
            </Link>
          </div>

          {/* Navigation — scrollable so the user/logout footer stays reachable on small PWA viewports */}
          <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <item.icon size={20} />
                <span className="text-sm uppercase tracking-wider">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User section — fixed at bottom so Logout is always reachable */}
          <div className="p-4 border-t border-border flex-shrink-0">
            <div className="mb-4 px-4">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-red-500/10 rounded text-xs text-red-400 uppercase">
                Admin
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} />
              <span className="text-sm uppercase tracking-wider">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PlatformAdminLayout;
